// Lightweight per-page SEO helper for the Vite+react-router stack.
// Updates <title>, meta description, canonical, og:title/description/url
// without pulling in react-helmet-async.

const SITE_ORIGIN = "https://cashbackbr.com.br";

const ensureMeta = (selector: string, create: () => HTMLMetaElement) => {
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = create();
    document.head.appendChild(tag);
  }
  return tag;
};

const DEFAULT_OG_IMAGE = "https://cashbackbr.com.br/og-image.jpg";

export function applySeo(opts: {
  title: string;
  description: string;
  path: string;
  ogType?: string;
  ogImage?: string;
}) {
  if (typeof document === "undefined") return;
  const url = `${SITE_ORIGIN}${opts.path}`;
  const image = opts.ogImage ?? DEFAULT_OG_IMAGE;
  document.title = opts.title;

  const setNamed = (name: string, content: string) => {
    const tag = ensureMeta(`meta[name="${name}"]`, () => {
      const m = document.createElement("meta");
      m.setAttribute("name", name);
      return m;
    });
    tag.setAttribute("content", content);
  };
  const setProp = (property: string, content: string) => {
    const tag = ensureMeta(`meta[property="${property}"]`, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", property);
      return m;
    });
    tag.setAttribute("content", content);
  };

  setNamed("description", opts.description);
  setProp("og:title", opts.title);
  setProp("og:description", opts.description);
  setProp("og:url", url);
  setProp("og:type", opts.ogType ?? "website");
  setProp("og:image", image);
  setProp("og:site_name", "CashbackBR");
  setProp("og:locale", "pt_BR");
  setNamed("twitter:card", "summary_large_image");
  setNamed("twitter:title", opts.title);
  setNamed("twitter:description", opts.description);
  setNamed("twitter:image", image);

  let canonical = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
}

export function injectJsonLd(id: string, data: unknown) {
  if (typeof document === "undefined") return;
  let tag = document.getElementById(id) as HTMLScriptElement | null;
  if (!tag) {
    tag = document.createElement("script");
    tag.id = id;
    tag.type = "application/ld+json";
    document.head.appendChild(tag);
  }
  tag.textContent = JSON.stringify(data);
}

export const SEO_SITE_ORIGIN = SITE_ORIGIN;

/**
 * Inject a WebPage + BreadcrumbList JSON-LD pair for a given route.
 * Replaces previous tags with the same id, so safe to call on every mount.
 */
export function injectPageStructuredData(opts: {
  title: string;
  description: string;
  path: string;
  breadcrumbs: Array<{ name: string; path: string }>;
}) {
  const url = `${SITE_ORIGIN}${opts.path}`;
  injectJsonLd("ld-webpage", {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: opts.title,
    description: opts.description,
    url,
    inLanguage: "pt-BR",
    isPartOf: {
      "@type": "WebSite",
      name: "CashbackBR",
      url: SITE_ORIGIN,
    },
  });
  injectJsonLd("ld-breadcrumb", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: opts.breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.name,
      item: `${SITE_ORIGIN}${b.path}`,
    })),
  });
}

/**
 * Marca a rota atual como noindex/nofollow para crawlers.
 * Útil em rotas utilitárias (auth, reset-password) e redirects (/index, /home)
 * que são intencionalmente excluídas do sitemap.
 */
export function applyNoIndex(opts?: { canonicalPath?: string }) {
  if (typeof document === "undefined") return;
  const tag = ensureMeta('meta[name="robots"]', () => {
    const m = document.createElement("meta");
    m.setAttribute("name", "robots");
    return m;
  });
  tag.setAttribute("content", "noindex, nofollow");

  if (opts?.canonicalPath) {
    let canonical = document.head.querySelector<HTMLLinkElement>(
      'link[rel="canonical"]',
    );
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_ORIGIN}${opts.canonicalPath}`);
  }
}
